import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { WHATSAPP_COLOR } from "../../../constants/socialColors.constant";
import { COLOR_BLACK } from "../../../constants/theme";
import { ContactSource } from "../../../cores/contacts/enum/ContactSource.enum";
import { NewContactsNotification } from "../../../cores/notifications/interfaces/api/get-notifications";
import "./NewContactsNotificationItem.scss";

interface Props {
  notification: NewContactsNotification;
  variant?: "dropdown" | "page";
  isDarkMode?: boolean;
}

export default function NewContactNotificationItem({
  notification,
  variant = "dropdown",
  isDarkMode = false,
}: Props) {
  const intl = useIntl();

  const title = useMemo(() => {
    switch (notification.origin) {
      case ContactSource.CHAT:
        return intl.formatMessage({ id: "app.notifications.contactOrigin.chat" });
      case ContactSource.FORM:
        return intl.formatMessage({ id: "app.notifications.contactOrigin.form" });
      case ContactSource.WHATSAPPCHAT:
        return intl.formatMessage({ id: "app.notifications.contactOrigin.whatsapp" });
      default:
        return "";
    }
  }, [notification.origin, intl]);

  const icon = useMemo(() => {
    switch (notification.origin) {
      case ContactSource.CHAT:
        return (
          <FontAwesomeIcon
            icon={["fas", "comments"]}
            className="mr-2"
            size="2x"
            color={COLOR_BLACK}
          />
        );
      case ContactSource.FORM:
        return (
          <FontAwesomeIcon
            icon={["fab", "wpforms"]}
            className="mr-2"
            size="2x"
            color={COLOR_BLACK}
          />
        );
      case ContactSource.WHATSAPPCHAT:
        return (
          <FontAwesomeIcon
            icon={["fab", "whatsapp"]}
            className="mr-2"
            size="2x"
            color={WHATSAPP_COLOR}
          />
        );
      default:
        return null;
    }
  }, [notification.origin]);

  const iconPage = useMemo(() => {
    switch (notification.origin) {
      case ContactSource.CHAT:
        return (
          <FontAwesomeIcon icon={["fas", "comments"]} size="2x" color={COLOR_BLACK} />
        );
      case ContactSource.FORM:
        return (
          <FontAwesomeIcon icon={["fab", "wpforms"]} size="2x" color={COLOR_BLACK} />
        );
      case ContactSource.WHATSAPPCHAT:
        return (
          <FontAwesomeIcon icon={["fab", "whatsapp"]} size="2x" color={WHATSAPP_COLOR} />
        );
      default:
        return null;
    }
  }, [notification.origin]);

  if (variant === "page") {
    return (
      <div
        className={cn("new-contact-notification-page", {
          "new-contact-notification-page--dark": isDarkMode,
        })}
      >
        <div className="new-contact-notification-page__icon">{iconPage}</div>
        <div className="new-contact-notification-page__body">
          <p className="new-contact-notification-page__eyebrow">
            {intl.formatMessage({ id: "app.notifications.kind.newContact" })}
          </p>
          <p className="new-contact-notification-page__title">{title}</p>
          <p className="new-contact-notification-page__success">
            <FontAwesomeIcon icon={["fas", "check-circle"]} className="mr-2" />
            {intl.formatMessage({ id: "app.notifications.contactCreatedSuccess" })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center border-bottom mb-1">
      {icon}
      <div className="d-flex flex-column overflow-hidden">
        <span className="capitalize">{title}</span>
        <span className="text-truncate text-success">
          <FontAwesomeIcon icon={["fas", "check"]} className="mr-2" />
          Nuevo contacto creado
        </span>
      </div>
    </div>
  );
}
